import { t as translate } from 'i18n';
/** Presentation controls keep the stored Cube bindings and exact-value semantics. */
export const MAX_FILTER_VALUES = 50;
export const MAX_FILTER_VALUE = 200;
const MANY = new Set(['multiselect', 'chips', 'checkboxes']);
const OPTIONS = new Set(['select', 'multiselect', 'radio', 'checkboxes']);
const DECIMAL = /^[+-]?(?:\d+(?:\.\d*)?|\.\d+)(?:[eE][+-]?\d+)?$/;

export function filterInput(spec) {
  return spec.input || (spec.kind === 'date' ? 'date_range' : 'text');
}

export function isISODate(value) {
  if (typeof value !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(value) || value.startsWith('0000')) return false;
  const date = new Date(`${value}T00:00:00Z`);
  return !Number.isNaN(date.getTime()) && date.toISOString().slice(0, 10) === value;
}

export function normalizedFilter(spec, value) {
  const input = filterInput(spec);
  if (value === undefined || value === null || value === '') return { empty: true };
  if (input === 'date_range') {
    if (Array.isArray(value) && value.length === 2 && !value[0] && !value[1]) return { empty: true };
    if (!Array.isArray(value) || value.length !== 2 || !value.every(isISODate) || value[0] > value[1]) {
      return { error: translate('assistant.dashboardFiltersEnterTheStartAndEndOfThe') };
    }
    return { value: [...value] };
  }
  const allowed = new Set((spec.options || []).map((option) => option.value));
  const validString = (item) => typeof item === 'string' && item.trim().length > 0 && item.length <= MAX_FILTER_VALUE;
  if (MANY.has(input)) {
    if (Array.isArray(value) && !value.length) return { empty: true };
    if (!Array.isArray(value) || value.length > MAX_FILTER_VALUES || !value.every(validString) || new Set(value).size !== value.length) {
      return { error: translate('assistant.dashboardFiltersSelectUpToValuesEachUpTo', { value1: spec.title, value2: MAX_FILTER_VALUES, value3: MAX_FILTER_VALUE }) };
    }
    if (OPTIONS.has(input) && value.some((item) => !allowed.has(item))) return { error: translate('assistant.dashboardFiltersSelectValuesFromTheList', { value1: spec.title }) };
    return { value: OPTIONS.has(input) ? spec.options.filter((option) => value.includes(option.value)).map((option) => option.value) : [...value].sort() };
  }
  if (typeof value !== 'string') return { error: translate('assistant.dashboardFiltersCheckTheField', { value1: spec.title }) };
  const next = OPTIONS.has(input) ? value : value.trim();
  if (!next) return { empty: true };
  if (!validString(next)) return { error: translate('assistant.dashboardFiltersNoMoreThanCharacters', { value1: spec.title, value2: MAX_FILTER_VALUE }) };
  if (OPTIONS.has(input) && !allowed.has(next)) return { error: translate('assistant.dashboardFiltersSelectAValueFromTheList', { value1: spec.title }) };
  if (input === 'number' && (!DECIMAL.test(next) || !Number.isFinite(Number(next)))) return { error: translate('assistant.dashboardFiltersEnterAValidNumber', { value1: spec.title }) };
  return { value: next };
}

export function appliedFilters(specs, draft) {
  const values = {};
  for (const spec of specs) {
    const result = normalizedFilter(spec, draft[spec.id]);
    if (result.error) return { error: result.error };
    if (!result.empty) values[spec.id] = result.value;
  }
  return { values };
}

function sameBinding(left, right) {
  const members = (spec) => [...new Set(spec.bindings.map((binding) => binding.member))].sort().join('\0');
  return left.kind === right.kind && filterInput(left) === filterInput(right) && members(left) === members(right);
}

export function inheritedFilterValue(source, spec, value) {
  const previous = source?.filters?.find((item) => item.id === spec.id);
  if (previous && !sameBinding(previous, spec)) return undefined;
  const result = normalizedFilter(spec, value);
  return result.error || result.empty ? undefined : result.value;
}
